/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { InputActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Button, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import G17Client from '../../../services/G17Client';
import { naturalSort } from '../../../utils';
import { Loader } from '@g17eco/atoms/loader';
import { BaseStepCompProps, IssuerListItem } from '../types';

interface ReferrerCodeIssuerLookupProps extends BaseStepCompProps {
  handleSelectIssuer: (issuer?: IssuerListItem) => void;
  description?: JSX.Element;
}

export const ReferrerCodeIssuerLookup = (props: ReferrerCodeIssuerLookupProps) => {
  const { updateForm, handleNext, handlePrevious, formData, setStepperStateData, description } = props;

  const [issuerList, setIssuerList] = useState<IssuerListItem[]>([]);
  const [isLoading, setLoading] = useState(false);
  const isFormValid = formData.initiativeName;

  const handleSelectIssuer = (issuerName: string, currentIssuerList: IssuerListItem[] = issuerList) => {
    updateForm('initiativeName', issuerName);
    const selectedIssuer = currentIssuerList.find((issuer) => issuer.name === issuerName);
    props.handleSelectIssuer(selectedIssuer);
    setStepperStateData(prevState => ({
      ...prevState,
      issuerId: selectedIssuer?._id
    }));
  }

  const handleGoToOtherServices = () => {
    updateForm('initiativeName', '');
    updateForm('metadata', {});
    handleNext();
  }

  const companyOptions = issuerList
    .map(issuer => ({ value: issuer.name, label: issuer.name }))
    .sort((a, b) => naturalSort(a.label, b.label));

  const fetchIssuers = () => {
    if (!formData.referralCode) {
      setIssuerList([]);
      handleSelectIssuer('', []);

      setStepperStateData(prevState => ({
        ...prevState,
        customCompanyEnabled: false
      }));
      return;
    }
    setLoading(true);
    G17Client.getIssuersByReferralCode(formData.referralCode)
      .then((data) => {
        setIssuerList(data.issuers ?? []);
        const isIssuer = data.issuers.find((issuer) => issuer.name === formData.initiativeName);
        handleSelectIssuer(isIssuer ? formData.initiativeName : '', data.issuers);

        setStepperStateData(prevState => ({
          ...prevState,
          customCompanyEnabled: data.customCompanyEnabled
        }))
      })
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchIssuers();
    // run on init only
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectValue = companyOptions.find((option) => option.value === formData.initiativeName) ?? {
    value: formData.initiativeName,
    label: formData.initiativeName,
  };

  const onInputChange = (value: string, action: InputActionMeta) => {
    if (['set-value', 'input-change'].includes(action.action)) {
      handleSelectIssuer(value);
    }
  };

  return (
    <div className='d-lg-flex justify-content-between onboarding-form'>
      {isLoading ? <Loader /> : null}
      <aside className='title-container'>
        <div className='h4 text-strong'>Company info</div>
        {description}
        <p>
          <Button color='link' size='sm' className='text-ThemeHeadingLight px-0' onClick={() => handleGoToOtherServices()}>
            Other services<i className='fas fa-angle-right ml-2' />
          </Button>
        </p>
      </aside>
      <section className='form-container'>
        <Form>
          <FormGroup className='mb-4'>
            <Label for='referralCode'>Enter your referral code</Label>
            <Input
              placeholder='Referral code'
              id='referralCode'
              name='referralCode'
              type='text'
              onChange={(e) => updateForm('referralCode', e.target.value.toUpperCase())}
              onBlur={fetchIssuers}
              value={formData.referralCode}
              required={true}
            />
            <FormText>If you were given one.</FormText>
          </FormGroup>
          <FormGroup className='mb-4'>
            <Label for='referralCode'>What is your company name?</Label>
            {issuerList.length ? (
              <CreatableSelect
                id='companyNameSelect'
                placeholder='Type in your company name'
                noOptionsMessage={() => 'Company not found'}
                options={companyOptions}
                isClearable
                formatCreateLabel={(inputValue: string) => `${inputValue}`}
                value={selectValue}
                onChange={(option) => handleSelectIssuer(option?.value ?? '')}
                onInputChange={onInputChange}
                components={formData.initiativeName ? {} : { ClearIndicator: () => null }}
              />
            ) : (
              <Input
                id='companyNameInput'
                placeholder='Type in your company name'
                type='text'
                onChange={(e) => handleSelectIssuer(e.target.value)}
                value={formData.initiativeName}
              />
            )}
            <FormText>If you do not find your company, please get in contact with our support team.</FormText>
          </FormGroup>
        </Form>
        <div className='mt-5 d-flex'>
          {handlePrevious ? <Button color='link-secondary' onClick={handlePrevious}><i className='far fa-angle-left mr-2' />Back</Button> : null}
          <Button className='ml-auto' color='primary' disabled={!isFormValid} onClick={handleNext}>Next<i className='fas fa-angle-right ml-2' /></Button>
        </div>
      </section>
    </div>
  );
}
